import { AlertTriangle, Check, Pencil } from "lucide-react";
import { useState } from "react";

export type OutOfWindowTxn = {
  readonly date: string;
  readonly description: string | null | undefined;
  readonly amount: number;
};

export type SourceWindow = {
  readonly key: string;
  readonly kind: string;
  readonly label: string;
  readonly dataFrom: string | null | undefined;
  readonly dataTo: string | null | undefined;
  readonly declaredFrom: string | null | undefined;
  readonly declaredTo: string | null | undefined;
  readonly effectiveFrom: string | null | undefined;
  readonly effectiveTo: string | null | undefined;
  readonly reason: string | null | undefined;
  readonly lateStart: boolean;
  readonly explained: boolean;
  readonly outOfWindowCount: number;
  readonly outOfWindowSample: ReadonlyArray<OutOfWindowTxn>;
};

export type WindowEdit = {
  declaredFrom: string | null;
  declaredTo: string | null;
  reason: string | null;
};

const KIND_LABEL: Record<string, string> = {
  BANK: "Bank",
  WALLET: "Wallet",
  STRIPE: "Stripe",
  QBO: "QuickBooks",
};

const money = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD" });

function blank(value: string) {
  return value.trim() === "" ? null : value.trim();
}

function WindowEditor({
  source,
  onSave,
  onCancel,
  saving,
}: {
  source: SourceWindow;
  onSave: (edit: WindowEdit) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [from, setFrom] = useState(source.declaredFrom ?? "");
  const [to, setTo] = useState(source.declaredTo ?? "");
  const [reason, setReason] = useState(source.reason ?? "");

  return (
    <tr className="bg-blue-50/40">
      <td colSpan={6} className="px-2 py-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-600">
            <span className="block">Covers from</span>
            <input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className="mt-0.5 rounded border px-2 py-1 text-xs"
            />
          </label>
          <label className="text-xs text-gray-600">
            <span className="block">to</span>
            <input
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className="mt-0.5 rounded border px-2 py-1 text-xs"
            />
          </label>
          <label className="min-w-[16rem] flex-1 text-xs text-gray-600">
            <span className="block">
              Why it starts when it does{" "}
              <span className="text-gray-400">
                — recording this clears the “starts late” flag for good
              </span>
            </span>
            <input
              type="text"
              value={reason}
              placeholder="e.g. the Wise account was opened in June 2022"
              onChange={(event) => setReason(event.target.value)}
              className="mt-0.5 w-full rounded border px-2 py-1 text-xs"
            />
          </label>

          <button
            type="button"
            disabled={saving}
            onClick={() =>
              onSave({
                declaredFrom: blank(from),
                declaredTo: blank(to),
                reason: blank(reason),
              })
            }
            className="rounded bg-gray-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-2 py-1.5 text-xs text-gray-500 hover:underline"
          >
            Cancel
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-400">
          Leave all three empty to drop the claim — the window then follows
          whatever the imported data says.
        </p>
      </td>
    </tr>
  );
}

// The evidence row. A count alone is an accusation; showing the actual
// transactions is what lets the operator tell "I forgot to import 2022" from
// "that vendor just happens to be called Wise Consulting".
function EvidenceRow({ source }: { source: SourceWindow }) {
  return (
    <tr className="bg-red-50/50">
      <td colSpan={6} className="px-2 pb-3 pt-1">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-red-800">
              {source.outOfWindowCount} transaction
              {source.outOfWindowCount === 1 ? "" : "s"} reference {source.label}{" "}
              outside this window — either the window is wrong, or history is
              missing.
            </p>
            <ul className="mt-1 space-y-0.5">
              {source.outOfWindowSample.map((txn, index) => (
                <li key={index} className="flex gap-3 text-xs text-red-900/80">
                  <span className="font-mono tabular-nums">{txn.date}</span>
                  <span className="min-w-0 flex-1 truncate">{txn.description}</span>
                  <span className="font-mono tabular-nums">{money(txn.amount)}</span>
                </li>
              ))}
              {source.outOfWindowCount > source.outOfWindowSample.length && (
                <li className="text-xs text-red-900/50">
                  …and {source.outOfWindowCount - source.outOfWindowSample.length} more
                </li>
              )}
            </ul>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function SourceWindowsPanel({
  sources,
  historyStartOn,
  onSaveWindow,
  savingKey,
}: {
  sources: ReadonlyArray<SourceWindow>;
  historyStartOn: string | null | undefined;
  onSaveWindow: (sourceKey: string, edit: WindowEdit) => void;
  savingKey: string | null;
}) {
  const [editing, setEditing] = useState<string | null>(null);

  if (sources.length === 0) {
    return null;
  }

  return (
    <div className="rounded border bg-white p-4">
      <h2 className="text-sm font-medium text-gray-700">Source coverage</h2>
      <p className="mb-2 text-xs text-gray-500">
        Each source covers the window its imported data implies, until you say
        otherwise.
        {historyStartOn
          ? ` Anything starting materially after ${historyStartOn} is flagged until you import the missing history or record why it starts late.`
          : " Set a history start above to have sources checked against an absolute floor rather than only against themselves."}
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-left text-gray-400">
              <th className="py-1 font-normal">Source</th>
              <th className="py-1 font-normal">Covers</th>
              <th className="py-1 font-normal">Imported</th>
              <th className="py-1 font-normal">Status</th>
              <th className="py-1 font-normal">Why</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {sources.map((source) => {
              const declared = source.declaredFrom != null || source.declaredTo != null;
              const flagged = source.outOfWindowCount > 0;
              const late = source.lateStart && !source.explained;

              return [
                <tr key={source.key} className="border-b last:border-b-0">
                  <td className="py-1.5 pr-3">
                    <span className="font-medium text-gray-900">{source.label}</span>
                    <span className="ml-1.5 text-gray-400">
                      {KIND_LABEL[source.kind] ?? source.kind}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 font-mono tabular-nums text-gray-700">
                    {source.effectiveFrom ?? "—"} → {source.effectiveTo ?? "—"}
                    {declared && (
                      <span className="ml-1.5 rounded bg-gray-100 px-1 text-[10px] text-gray-500">
                        declared
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 font-mono tabular-nums text-gray-400">
                    {source.dataFrom ?? "—"} → {source.dataTo ?? "—"}
                  </td>
                  <td className="py-1.5 pr-3">
                    {flagged ? (
                      <span className="font-medium text-red-600">Contradicted</span>
                    ) : late ? (
                      <span className="font-medium text-amber-600">Starts late</span>
                    ) : source.explained ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600">
                        <Check className="h-3 w-3" /> Explained
                      </span>
                    ) : (
                      <span className="text-emerald-600">OK</span>
                    )}
                  </td>
                  <td className="max-w-[18rem] truncate py-1.5 pr-3 text-gray-500">
                    {source.reason}
                  </td>
                  <td className="py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() =>
                        setEditing((current) => (current === source.key ? null : source.key))
                      }
                      className="inline-flex items-center gap-1 text-gray-400 hover:text-gray-700"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  </td>
                </tr>,

                editing === source.key ? (
                  <WindowEditor
                    key={`${source.key}-edit`}
                    source={source}
                    saving={savingKey === source.key}
                    onCancel={() => setEditing(null)}
                    onSave={(edit) => {
                      onSaveWindow(source.key, edit);
                      setEditing(null);
                    }}
                  />
                ) : null,

                flagged ? <EvidenceRow key={`${source.key}-evidence`} source={source} /> : null,
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
